import React, { Component, ReactNode } from 'react';
import { RuleJsonModel } from '../../domain/collection/rule/FlowDomains';
import CustomTable, { CustomColumnProps } from '../custom/CustomTable';
import { DataTableRowClickEventParams } from 'primereact/datatable';
import ItemStatusComponent from '../ItemStatusComponent';
import LanguageUtil from '../../util/LanguageUtil';
import RuleService from '../../service/RuleService';
import { ConfirmPopup } from 'primereact/confirmpopup';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import FolderView from '../folder/view/FolderView';
import RoleHelper from '../../RoleHelper';
import ListDraggableItem from '../custom/ListDraggableItem';
import { dispatch, RootState } from '../../redux/store';
import { ComponentState, setSelectedItems } from '../../redux/actions/ComponentActions';
import { connect, ConnectedProps } from 'react-redux';
import EmptyComponent from '../custom/empty-components/EmptyComponent';
import { customConfirm } from '../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../deploy/UndeployImpactDialog';
import Icon from '../../icons/Icon';
import { query } from '@trlogic/client-js';
import { Tooltip } from 'primereact/tooltip';
import ComponentName from '../../constant/ComponentName';
import { PermissionType } from '../../domain/rbac/RbacDomains';
import CustomTableContentWrapper from '../custom/CustomTableContentWrapper';
import ComponentUtil from '../../util/ComponentUtil';
import moment from 'moment/moment';
import Order = query.Order;
import WorkflowNodeUtil from "../../util/WorkflowNodeUtil";

class RuleListViewComponent extends Component<Props> {
  private ruleService: RuleService = ServiceContainer.getInstance().get(ServiceNames.FLOW);

  public render(): ReactNode {
    const {
      rules,
      loading,
      views,
      component: { selectedComponentIds },
      onSortChange,
    } = this.props;
    return views && views.length > 0 && rules && rules.length > 0 ? (
      <div className={'overflow-y-scroll max-h-[calc(100dvh-430px)] scrollable-editor-container 2xl:max-h-[calc(100dvh-460px)] flex w-full  rounded-lg'}>
        <ConfirmPopup />
        <CustomTable
          selectionMode='checkbox'
          selection={selectedComponentIds}
          onSelectionChange={this.onSelectionChange}
          onRowClick={this.openEditor}
          componentName={ComponentName.RULE}
          openNewTab={true}
          columns={this.getColumns()}
          loading={loading}
          rowClassName={(data) => {
            const view = views.find((v: FolderView) => v.getModel().value?.payload.name == data.name);
            return view ? `list_${view?.getModel().id}` : '';
          }}
          onSortChange={onSortChange}
          value={rules}
        />
        <ListDraggableItem itemType={ComponentName.RULE} currentModal={this.props.currentModal} />
      </div>
    ) : (
      <EmptyComponent type={ComponentName.RULE} />
    );
  }

  private onSelectionChange = (e: any) => {
    dispatch(setSelectedItems(e.value));
  };

  private openEditor = (e: DataTableRowClickEventParams) => {
    const { openRule } = this.props;
    const name = e.data.name;
    openRule(name);
  };

  private getColumns = (): CustomColumnProps[] => {
    const columns: CustomColumnProps[] = [
      {
        field: 'name',
        header: LanguageUtil.get({ id: `pages.rule.table.name`, defaultMessage: 'Name' }),
        className: 'min-w-[11%]',
        sortable: true,
        copyable: true,
        onCopy: (e: RuleJsonModel) => {
          return e.name;
        },
        body: (record: RuleJsonModel) => {
          const { views } = this.props;
          const view: FolderView = views.find((view: FolderView) => view.getModel().value?.payload.name == record.name);
          return (
            <p className={'text-left flex items-center text-xs 2xl:text-md'}>
              <div id={`list_${view?.getModel().id}`} />
              <CustomTableContentWrapper clipSize={16} showEllipsis={true} text={record.name} data-testid={`${ComponentName.RULE}_name_text_${record.name}`} />
            </p>
          );
        },
      },
      {
        field: 'stage',
        header: LanguageUtil.get({ id: `pages.rule.table.status`, defaultMessage: 'Status' }),
        className: 'w-1/10',
        body: (record: RuleJsonModel) => {
          return (
            <div className={' w-full flex justify-start items-center  '}>
              <ItemStatusComponent status={record.stage} containerClassName='justify-center' data-testid={`${ComponentName.RULE}_stage_text_${record.name}`} />
            </div>
          );
        },
      },
      {
        field: 'type',
        header: LanguageUtil.get({ id: `pages.rule.table.type`, defaultMessage: 'Type' }),
        className: 'w-1/10',
        body: (record: RuleJsonModel) => {
          return (
            <div className='flex items-center justify-center bg-f-gray-5 text-f-black-2 px-2.5 py-[1px] rounded-full w-[84px] h-6 text-center text-sm'>
              {LanguageUtil.get({ id: `pages.rule.types.${record.type || 'monitoring'}`, defaultMessage: record.type || 'Monitoring' })}
            </div>
          );
        },
      },
      {
        field: 'description',
        header: LanguageUtil.get({ id: `pages.rule.table.description`, defaultMessage: 'Description' }),
        className: 'min-w-[15%]',
        sortable: true,
        body: (record: RuleJsonModel) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 justify-start text-xs 2xl:text-md relative group'}>
              <CustomTableContentWrapper
                clipSize={32}
                text={
                  record.description ||
                  LanguageUtil.get({ id: 'common.label.noDescription', defaultMessage: 'No description' })
                }
              />
            </div>
          );
        },
      },
      {
        field: 'workflow',
        header: LanguageUtil.get({ id: `pages.rule.table.channel`, defaultMessage: 'Channel' }),
        className: 'min-w-[10%]',
        sortable: true,
        body: (record: any) => {
          const transaction = WorkflowNodeUtil.getTransaction(record.workflow)?.value?.name ?? 'N/A';
          return (
            <div className={'text-left flex items-center text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={transaction} clipSize={15} showEllipsis={true} />
            </div>
          );
        },
      },
      {
        field: 'priority',
        header: LanguageUtil.get({ id: `pages.rule.table.priority`, defaultMessage: 'Priority' }),
        body: (record: RuleJsonModel) => {
          return (
            <p className={'flex justify-start items-center text-xs 2xl:text-md'}>
              <CustomTableContentWrapper
                text={record.priority?.toString() || LanguageUtil.get({ id: `pages.rule.priorityNotAdded`, defaultMessage: 'Priority not added.' })}
              />
            </p>
          );
        },
      },
      {
        field: 'score',
        header: LanguageUtil.get({ id: `pages.rule.table.score`, defaultMessage: 'Score' }),
        sortable: true,
        body: function (record: RuleJsonModel) {
          const resolutions = WorkflowNodeUtil.getResolution(record.workflow);
          const score = record.score ?? resolutions?.value?.score ?? 'None';
          return (
            <div
              className={`flex flex-col justify-center items-center ${score == 0 ? 'bg-[#DBF8E5] text-[#3F9E2C]' : score == 100 ? 'bg-[#FFF1F3] text-[#D2332F]' : 'bg-[#FCF1BB] text-[#C57500]'} w-[40px] text-xs 2xl:text-md rounded-[4px] h-[22px]`}
            >
              <CustomTableContentWrapper text={score.toString()} />
            </div>
          );
        },
      },
      {
        field: 'createdAt',
        header: LanguageUtil.get({ id: `pages.rule.table.createdAt`, defaultMessage: 'CreatedAt' }),
        className: 'min-w-[11%]',
        body: (record: RuleJsonModel) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.createdAt ? moment(record.createdAt).format('LLL') : ''} />
            </div>
          );
        },
      },
      {
        field: 'updatedAt',
        header: LanguageUtil.get({ id: `pages.rule.table.updatedAt`, defaultMessage: 'UpdatedAt' }),
        className: 'min-w-[11%]',
        body: (record: RuleJsonModel) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.updatedAt ? moment(record.updatedAt).format('LLL') : ''} />
            </div>
          );
        },
      },
    ];
    if (RoleHelper.canEdit('Rule')) {
      columns.push({
        field: 'action',
        header: LanguageUtil.get({ id: `pages.rule.table.actions`, defaultMessage: 'Actions' }),
        className: 'max-w-[75px] 2xl:max-w-[100px]',
        sortable: false,
        body: this.getActions,
      });
    }
    return columns;
  };

  private getActions = (record: RuleJsonModel) => {
    const { permissions } = this.props;
    const actions: ReactNode[] = [];
    const permission = permissions[record.name];
    const allowed = ['Edit', 'Admin'];

    if (!allowed.includes(permission)) {
      return null;
    }
    const isLocked = ComponentUtil.isLocked(ComponentName.RULE, record.name);
    if (isLocked) {
      return null;
    }

    const canRemove = record.stage == 'DRAFT';
    const canUndeploy = record.stage != 'DRAFT';

    if (canUndeploy) {
      actions.push(
        <div
          key='undeploy'
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            customConfirm({
              target: e.currentTarget,
              accept: async () => confirmUndeployWithImpact(ComponentName.RULE, [record.name], () => this.ruleService.undeployFlow(record.name)),
              message: LanguageUtil.get({ id: 'messages.confirm.undeploy1', defaultMessage: 'Are you sure you want to undeploy?' }),
            });
          }}
          data-testid={`${ComponentName.RULE}_undeploy_button_${record.name}`}
        >
          <Tooltip position={'left'} target={'.undeploy'}>
            {LanguageUtil.get({ id: 'common.label.undeploy', defaultMessage: 'Undeploy' })}
          </Tooltip>
          <Icon icon={'undeploy'} size={20} className={'undeploy hover:text-primary text-[#607080] hover:cursor-pointer'} />
        </div>,
      );
    }
    if (canRemove) {
      actions.push(
        <div
          key='remove'
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            customConfirm({
              target: e.currentTarget,
              accept: async () => await this.ruleService.removeFlow(record.name),
              message: LanguageUtil.get({ id: 'messages.confirm.confirmRemove', defaultMessage: 'Are you sure you want to remove?' }),
            });
          }}
          data-testid={`${ComponentName.RULE}_remove_button_${record.name}`}
        >
          <Tooltip position={'left'} target={'.remove'}>
            {LanguageUtil.get({ id: 'common.label.remove', defaultMessage: 'Remove' })}
          </Tooltip>
          <Icon
            icon={'delete'}
            size={20}
            color={'primary'}
            className={'remove hover:text-primary text-[#607080] hover:cursor-pointer flex items-center justify-center'}
          />
        </div>,
      );
    }
    actions.push(
      <div
        key='deploy'
        onClick={(e: any) => {
          e.stopPropagation();
          e.preventDefault();
          customConfirm({
            target: e.currentTarget,
            accept: async () => await this.ruleService.deployFlow(record),
            message: LanguageUtil.get({ id: 'messages.confirm.deploy1', defaultMessage: 'Are you sure you want to deploy?' }),
          });
        }}
        data-testid={`${ComponentName.RULE}_deploy_button_${record.name}`}
      >
        <Tooltip position={'left'} target={'.deploy'}>
          {LanguageUtil.get({ id: 'common.label.deploy', defaultMessage: 'Deploy' })}
        </Tooltip>
        <Icon icon={'deploy'} size={20} className={'deploy hover:text-primary text-[#607080] hover:cursor-pointer'} />
      </div>,
    );

    return <div className={'flex w-full justify-start items-center gap-x-4'}>{actions}</div>;
  };
}

interface Props extends ReduxProps {
  rules: RuleJsonModel[];
  openRule: (id: string) => void;
  views: any;
  currentModal: any;
  loading: boolean;
  onSortChange: (sort: Order[]) => void;
  permissions: Record<string, PermissionType>;
}

const mapStateToProps = (state: RootState) => ({
  component: state.component as ComponentState,
});
const mapDispatchToProps = {};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(RuleListViewComponent);
